import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  Target, 
  TrendingUp, 
  Heart, 
  Brain, 
  Sun,
  Moon,
  CheckCircle,
  ArrowRight,
  X
} from 'lucide-react';

interface CheckInInstructionsProps {
  onClose: () => void;
  checkInType: 'morning' | 'evening';
}

export default function CheckInInstructions({ onClose, checkInType }: CheckInInstructionsProps) {
  const [step, setStep] = useState(0);

  const morningSteps = [
    {
      icon: <Sun className="w-12 h-12 text-primary" />,
      title: "Welcome to Your Morning Check-In",
      description: "Take 2-5 minutes to set your intentions and understand how you're starting your day.",
      details: [
        "Connect with how you're feeling physically, emotionally, mentally, spiritually and relationally",
        "Track habits that support your wellbeing",
        "Set your focus for the day ahead"
      ]
    },
    {
      icon: <Target className="w-12 h-12 text-primary" />,
      title: "Why This Matters",
      description: "Daily check-ins help you build self-awareness and identify patterns over time.",
      details: [
        "Spot trends in your energy, mood, and habits",
        "Understand what supports your wellbeing",
        "Make informed decisions about your health",
        "Build consistency and celebrate progress"
      ]
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-accent" />,
      title: "You'll Track:",
      description: "Choose the areas that matter most to you.",
      details: [
        "Physical: Energy, sleep, exercise, nutrition",
        "Emotional: Mood, stress, connections",
        "Mental: Focus, clarity, mindfulness",
        "Spiritual: Purpose, alignment, gratitude",
        "Habits: Custom tracking for your goals"
      ]
    }
  ];

  const eveningSteps = [
    {
      icon: <Moon className="w-12 h-12 text-primary" />,
      title: "Welcome to Your Evening Reflection",
      description: "Take a moment to reflect on your day and prepare for restful sleep.",
      details: [
        "Review how your day unfolded",
        "Celebrate your wins, big and small",
        "Release what didn't serve you",
        "Set intentions for tomorrow"
      ]
    },
    {
      icon: <Heart className="w-12 h-12 text-accent" />,
      title: "The Power of Reflection",
      description: "Evening check-ins help you process your day and sleep better.",
      details: [
        "Process emotions and experiences",
        "Acknowledge progress and growth",
        "Let go of stress before bed",
        "Prepare your mind for rest"
      ]
    },
    {
      icon: <Brain className="w-12 h-12 text-primary" />,
      title: "You'll Reflect On:",
      description: "A gentle review of your day.",
      details: [
        "What went well today?",
        "What challenges did you face?",
        "What are you grateful for?",
        "How ready are you for sleep?",
        "What's one intention for tomorrow?"
      ]
    }
  ];

  const steps = checkInType === 'morning' ? morningSteps : eveningSteps;
  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <Card className="max-w-2xl w-full p-8 animate-scale-in relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
        
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 z-10"
        >
          <X className="w-4 h-4" />
        </Button>

        {/* Content */}
        <div className="relative space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 animate-bounce-gentle">
              {currentStep.icon}
            </div>
          </div>

          {/* Title */}
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent animate-slide-up">
              {currentStep.title}
            </h2>
            <p className="text-lg text-muted-foreground animate-slide-up" style={{ animationDelay: '0.1s' }}>
              {currentStep.description}
            </p>
          </div>

          {/* Details */}
          <div className="space-y-3 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            {currentStep.details.map((detail, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-all hover:scale-105"
                style={{ animationDelay: `${0.3 + index * 0.1}s` }}
              >
                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <p className="text-foreground">{detail}</p>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-6">
            {/* Progress dots */}
            <div className="flex gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === step 
                      ? 'w-8 bg-primary' 
                      : 'w-2 bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              {step < steps.length - 1 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="gap-2 hover:scale-105 transition-transform"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={onClose}
                  className="gap-2 bg-gradient-to-r from-primary to-accent hover:scale-105 transition-transform"
                >
                  <Sparkles className="w-4 h-4" />
                  Start Your Check-In
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
